import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import useMediaQuery from '@mui/material/useMediaQuery'
import CloseIcon from '@mui/icons-material/Close'
import useIsMobile from '../../../helpers/useIsMobile'
import { completionHint, dwellSecondsFor, isActivityComplete, isDwellBlock } from '../../helpers/blockRules'
import { errorMessage, isOk } from '../../helpers/trainingApi'
import BlockRenderer from '../blocks/BlockRenderer'
import CompletionPanel from './CompletionPanel'
import ModuleOutline from './ModuleOutline'
import NavFooter from './NavFooter'
import PlayerHeader from './PlayerHeader'
import { AssessmentReadyBanner, RevisitBanner, ReviewerBanner, SaveNotice, TestOutBanner } from './PlayerBanners'
import { colours, playerRootSx } from './playerStyles'
import { canOpen, flattenScreens, isComplete, resumeIndex } from './screens'
import useProgressSender from './useProgressSender'

const SAVE_FAILED = 'We could not save your progress. Check your connection.'
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
const isRefusal = (res) => res?.status === 409

// A 409 is the server declining (a lock); anything else that is not OK is
// treated as a failed save the learner can retry.
const noticeFor = (res, retry) =>
  isRefusal(res)
    ? { severity: 'warning', message: errorMessage(res, 'This screen is not finished yet.') }
    : { severity: 'error', message: SAVE_FAILED, retry }

const Player = ({ enrolmentId, data, onRefresh }) => {
  const { content, enrolment, assessment, locksOff, feedbackSubmitted } = data
  const screens = useMemo(() => flattenScreens(content.modules), [content.modules])
  const [index, setIndex] = useState(() => resumeIndex(screens, data.progress, locksOff))
  const { block, module } = screens[index]

  const { blocks, blocksRef, send, heartbeat } = useProgressSender(enrolmentId, data.progress?.blocks, block.id)
  // What the learner has done on each activity this session. It outlives the
  // block component, so going Back and returning (or a failed save) never
  // loses an answer or a reflection.
  const [localStates, setLocalStates] = useState({})
  const [notice, setNotice] = useState(null)
  const [busy, setBusy] = useState(false)
  const [dwellReadyId, setDwellReadyId] = useState(null)
  const [unlockedHereId, setUnlockedHereId] = useState(null)
  const [seenNonce, setSeenNonce] = useState(0)
  const completing = useRef(new Set())
  const headingRef = useRef(null)
  const firstScreen = useRef(true)

  const isMobile = useIsMobile()
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)', { noSsr: true })
  const [outlineOpen, setOutlineOpen] = useState(!isMobile)

  const completed = isComplete(blocks, block.id)
  const completedCount = screens.filter((screen) => isComplete(blocks, screen.block.id)).length
  const allComplete = completedCount === screens.length
  const isLast = index === screens.length - 1
  // A block type this player does not know has no activity to finish, so it
  // unlocks on reading time like a text screen (the server accepts it too).
  const dwell = isDwellBlock(block) || isActivityComplete(block, {})
  const locked = !locksOff && !completed && !(dwell && dwellReadyId === block.id)
  const revisitIds = assessment?.reason === 'revisit' ? (assessment.revisit ?? []) : null

  const revisitModules = useMemo(
    () =>
      (revisitIds ?? [])
        .map((id) => ({
          module: content.modules.find((candidate) => candidate.id === id),
          firstIndex: screens.findIndex((screen) => screen.module.id === id),
        }))
        .filter((entry) => entry.module && entry.firstIndex !== -1),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [content.modules, screens, (revisitIds ?? []).join('|')],
  )

  const goTo = useCallback(
    (nextIndex) => {
      if (nextIndex < 0 || nextIndex >= screens.length || nextIndex === index) return
      if (!canOpen(screens, blocksRef.current, nextIndex, locksOff)) return
      // Credit the time spent here to this screen before the next one opens.
      heartbeat()
      setNotice(null)
      setIndex(nextIndex)
      if (isMobile) setOutlineOpen(false)
    },
    [screens, index, locksOff, heartbeat, isMobile, blocksRef],
  )

  // Opening a screen: tell the server (this starts the reading timer and
  // counts towards "look at these modules again"), then time the unlock.
  useEffect(() => {
    let cancelled = false
    let timer
    const hadFirstSeen = !!blocksRef.current[block.id]?.firstSeenAt
    send({ blockId: block.id, event: 'seen' }).then((res) => {
      if (cancelled) return
      if (!isOk(res)) {
        setNotice(noticeFor(res, () => {
          setNotice(null)
          setSeenNonce((value) => value + 1)
        }))
        return
      }
      const entry = res.data?.block ?? {}
      if (dwell && !entry.completedAt) {
        const total = dwellSecondsFor(block) * 1000
        // On a first visit the wait runs from now, which is immune to the
        // device clock being wrong. On a return visit it runs from the
        // server's first-seen time.
        const elapsed = hadFirstSeen && entry.firstSeenAt ? Date.now() - new Date(entry.firstSeenAt).getTime() : 0
        const remaining = Math.min(Math.max(total - elapsed, 0), total)
        timer = setTimeout(() => {
          setDwellReadyId(block.id)
          setUnlockedHereId(block.id)
        }, remaining)
      }
      // The server reopens the assessment once every screen of the missed
      // modules has been seen again.
      const next = screens[index + 1]
      if (revisitIds && (!next || next.module.id !== module.id)) onRefresh()
    })
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id, seenNonce])

  // Move focus to the new screen's heading so keyboard and screen reader
  // users start at the top of it.
  useEffect(() => {
    if (firstScreen.current) {
      firstScreen.current = false
      return
    }
    headingRef.current?.focus()
    headingRef.current?.scrollIntoView?.({ block: 'start', behavior: 'auto' })
  }, [block.id])

  const afterComplete = useCallback(
    (res) => {
      // Finishing the last outstanding screen opens the assessment.
      if (res.data?.completedCount >= res.data?.totalCount) onRefresh()
    },
    [onRefresh],
  )

  const completeActivity = useCallback(
    async (target, state) => {
      if (completing.current.has(target.id)) return false
      completing.current.add(target.id)
      const res = await send({ blockId: target.id, event: 'complete', state })
      completing.current.delete(target.id)
      if (!isOk(res)) {
        setNotice(noticeFor(res, () => {
          setNotice(null)
          completeActivity(target, state)
        }))
        return false
      }
      setUnlockedHereId(target.id)
      afterComplete(res)
      return true
    },
    [send, afterComplete],
  )

  const handleStateChange = useCallback(
    (state) => {
      setLocalStates((previous) => ({ ...previous, [block.id]: state }))
      // Reflections are saved when the learner presses Save, not as they type.
      if (block.type === 'reflection') return
      if (isActivityComplete(block, state) && !isComplete(blocksRef.current, block.id)) {
        completeActivity(block, state)
      }
    },
    [block, blocksRef, completeActivity],
  )

  const handleSave = useCallback(
    (state) => {
      setLocalStates((previous) => ({ ...previous, [block.id]: state }))
      setNotice(null)
      return completeActivity(block, state)
    },
    [block, completeActivity],
  )

  const handleNext = async () => {
    if (busy) return
    if (!completed) {
      setBusy(true)
      setNotice(null)
      const body = { blockId: block.id, event: 'complete', state: localStates[block.id] }
      let res = await send(body)
      // The server's clock can be a moment behind ours on a reading screen.
      if (isRefusal(res) && res.data?.errorCode === 'trainingIncomplete' && dwell) {
        await wait(1000)
        res = await send(body)
      }
      setBusy(false)
      if (!isOk(res)) {
        setNotice(noticeFor(res, handleNext))
        return
      }
      afterComplete(res)
    }
    if (!isLast) goTo(index + 1)
  }

  let hint = ''
  if (locked) hint = completionHint(block)
  else if (!locksOff && unlockedHereId === block.id && !isLast) hint = 'You can now go to the next screen.'

  const showCompletion = isLast && completed && (allComplete || locksOff)
  const assessmentOpen = !!assessment?.open && !enrolment?.passed
  const savedState = localStates[block.id] ?? blocks[block.id]?.state

  const outline = (
    <ModuleOutline
      modules={content.modules}
      screens={screens}
      blocks={blocks}
      currentIndex={index}
      locksOff={locksOff}
      onJump={goTo}
    />
  )

  return (
    <Box sx={{ ...playerRootSx, width: '100%', maxWidth: 1180, mx: 'auto', px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 }, boxSizing: 'border-box' }}>
      <PlayerHeader
        title={content.course.title}
        moduleTitle={module.title}
        screenNumber={index + 1}
        totalScreens={screens.length}
        completedCount={completedCount}
        outlineOpen={outlineOpen}
        onToggleOutline={() => setOutlineOpen((open) => !open)}
      />

      {locksOff && <ReviewerBanner enrolmentId={enrolmentId} />}
      {assessmentOpen && enrolment?.testOut && <TestOutBanner enrolmentId={enrolmentId} />}
      {assessmentOpen && !enrolment?.testOut && allComplete && !showCompletion && (
        <AssessmentReadyBanner enrolmentId={enrolmentId} />
      )}
      {revisitModules.length > 0 && <RevisitBanner modules={revisitModules} onJump={goTo} />}

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
        {!isMobile && outlineOpen && (
          <Paper
            variant="outlined"
            sx={{ width: 320, flexShrink: 0, p: 1.5, borderColor: colours.hairline, maxHeight: '80vh', overflowY: 'auto', position: 'sticky', top: 16 }}
          >
            {outline}
          </Paper>
        )}

        <Box component="section" aria-label="Course content" sx={{ flex: 1, minWidth: 0 }}>
          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 4 }, borderColor: colours.hairline, overflowWrap: 'break-word' }}>
            <BlockRenderer
              key={block.id}
              block={block}
              module={module}
              content={content}
              initialState={savedState}
              completed={completed}
              onStateChange={handleStateChange}
              onSave={handleSave}
              headingRef={headingRef}
            />
          </Paper>

          {notice && (
            <Box sx={{ mt: 2 }}>
              <SaveNotice notice={notice} onDismiss={() => setNotice(null)} />
            </Box>
          )}

          <NavFooter
            showBack={index > 0}
            onBack={() => goTo(index - 1)}
            showNext={!showCompletion}
            nextLabel={isLast ? 'Finish' : 'Next'}
            nextLocked={locked}
            busy={busy}
            onNext={handleNext}
            hint={hint}
          />

          {showCompletion && (
            <CompletionPanel
              enrolmentId={enrolmentId}
              enrolment={enrolment}
              assessment={assessment}
              feedbackSubmitted={feedbackSubmitted}
              revisitTitles={revisitModules.map((entry) => entry.module.title)}
            />
          )}
        </Box>
      </Box>

      <Drawer
        anchor="left"
        open={isMobile && outlineOpen}
        onClose={() => setOutlineOpen(false)}
        transitionDuration={reducedMotion ? 0 : undefined}
        PaperProps={{ sx: { width: 'min(340px, 88vw)' } }}
      >
        <Box sx={{ ...playerRootSx, p: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton onClick={() => setOutlineOpen(false)} aria-label="Close course outline">
              <CloseIcon />
            </IconButton>
          </Box>
          {outline}
        </Box>
      </Drawer>
    </Box>
  )
}

export default Player
